import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return { user: data.user };
    } catch (e) {
      return { error: apiError(e.response?.data?.detail, e.message) };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (!data.requires_confirmation && data.user) setUser(data.user);
      return data;
    } catch (e) {
      return { error: apiError(e.response?.data?.detail, e.message) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(false);
  }, []);

  const confirmSession = useCallback(async (session) => {
    try {
      const { data } = await api.post("/auth/session", session);
      setUser(data.user);
      return { user: data.user };
    } catch (e) {
      return { error: apiError(e.response?.data?.detail, e.message) };
    }
  }, []);

  const resendConfirmation = useCallback(async (email) => {
    try {
      const { data } = await api.post("/auth/resend", { email });
      return data;
    } catch (e) {
      return { error: apiError(e.response?.data?.detail, e.message) };
    }
  }, []);

  const homeFor = (u) => {
    if (!u) return "/login";
    if (u.role === "admin") return "/admin";
    if (u.role === "lab_technician" || u.role === "lab_reviewer") return "/lab";
    return "/account";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, confirmSession, resendConfirmation, homeFor }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
